<template>
  <div>
    <v-container fluid>
      <v-card elevation="3" rounded="xl">
        <v-card-title class="py-4 px-6">
          <v-row class="w-100 ma-0" align="center">
            <v-col cols="12" md="6" class="pa-0">
              <h2 class="text-h5 font-weight-bold primary--text">RMC Marketplace</h2>
              <p class="text-body-2 grey--text ma-0">Manage marketplace materials and pricing</p>
            </v-col>
            <v-col cols="12" md="6" class="pa-0 text-right">
              <v-chip color="primary" outlined class="mr-3">{{ count }} Materials</v-chip>
              <v-btn color="primary" rounded @click="openAdd">
                <v-icon left small>mdi-plus</v-icon>
                Add Material
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="mx-6"></v-divider>

        <v-card-subtitle class="px-6 pt-4 pb-2">
          <v-row>
            <v-col cols="12" md="3">
              <v-select v-model="filter.category" :items="categories" label="Category" outlined rounded dense clearable @change="getData"></v-select>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <v-card-text class="pa-0">
          <section v-if="isLoading" style="height: 300px" class="d-flex align-center justify-center">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          </section>

          <v-data-table v-else :headers="headers" :items="materials" hide-default-footer>
            <template #[`item.sr_no`]="{ index }">
              <span class="font-weight-medium">{{ index + 1 }}</span>
            </template>

            <template #[`item.category`]="{ item }">
              <v-chip :color="categoryColor(item.category)" small outlined>{{ item.category }}</v-chip>
            </template>

            <template #[`item.name`]="{ item }">
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption grey--text">{{ item.brand }} - {{ item.grade }}</div>
              </div>
            </template>

            <template #[`item.price`]="{ item }">
              <span class="font-weight-medium">₹{{ formatNumber(item.price_per_unit) }}/{{ item.unit }}</span>
            </template>

            <template #[`item.min_order`]="{ item }">
              {{ item.min_order_qty || '—' }} {{ item.unit }}
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn small icon color="primary" class="mr-1" @click="openEdit(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn small icon color="error" @click="deleteMaterial(item.id)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template #no-data>
              <div class="text-center py-12">
                <v-icon size="60" color="grey lighten-3">mdi-store</v-icon>
                <h3 class="grey--text mt-4">No Marketplace Items</h3>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add / Edit Material Dialog -->
      <v-dialog v-model="showDialog" max-width="500">
        <v-card rounded="lg">
          <v-card-title class="pa-6 pb-2">{{ isEditing ? 'Edit Material' : 'Add Marketplace Material' }}</v-card-title>
          <v-card-text class="px-6">
            <v-text-field v-model="form.name" label="Name" outlined dense class="mb-2"></v-text-field>
            <v-text-field v-model="form.brand" label="Brand" outlined dense class="mb-2"></v-text-field>
            <v-select v-model="form.category" :items="categories" label="Category" outlined dense class="mb-2"></v-select>
            <v-text-field v-model="form.grade" label="Grade" outlined dense class="mb-2"></v-text-field>
            <v-row>
              <v-col cols="6">
                <v-text-field v-model="form.price_per_unit" label="Price per unit" type="number" outlined dense></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-select v-model="form.unit" :items="['tonnes', 'litres', 'm³']" label="Unit" outlined dense></v-select>
              </v-col>
            </v-row>
            <v-text-field v-model="form.min_order_qty" label="Min order quantity" type="number" outlined dense></v-text-field>
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-spacer></v-spacer>
            <v-btn text @click="showDialog = false">Cancel</v-btn>
            <v-btn color="primary" rounded :loading="isSaving" @click="saveMaterial">{{ isEditing ? 'Save Changes' : 'Add' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "RmcMarketplacePage",
  data() {
    return {
      materials: [],
      count: 0,
      isLoading: false,
      isSaving: false,
      showDialog: false,
      isEditing: false,
      editingId: null,
      filter: { category: "" },
      categories: ["cement", "aggregates", "admixtures"],
      form: { name: "", brand: "", category: "cement", grade: "", price_per_unit: "", unit: "tonnes", min_order_qty: "" },
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: "50px" },
        { text: "Category", value: "category", width: "120px" },
        { text: "Material", value: "name", width: "250px" },
        { text: "Price", value: "price", width: "150px" },
        { text: "Min Order", value: "min_order", width: "130px" },
        { text: "", value: "actions", sortable: false, width: "80px" },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filter.category) params.category = this.filter.category;
        const query = new URLSearchParams(params);
        const response = await axios.get(`rmc_raw_materials/marketplace?${query}`);
        this.materials = response.data.data || [];
        this.count = response.data.count || 0;
      } catch (e) {
        console.error("Error fetching marketplace:", e);
      }
      this.isLoading = false;
    },
    openAdd() {
      this.isEditing = false;
      this.editingId = null;
      this.form = { name: "", brand: "", category: "cement", grade: "", price_per_unit: "", unit: "tonnes", min_order_qty: "" };
      this.showDialog = true;
    },
    openEdit(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.form = {
        name: item.name,
        brand: item.brand,
        category: item.category,
        grade: item.grade,
        price_per_unit: item.price_per_unit,
        unit: item.unit,
        min_order_qty: item.min_order_qty,
      };
      this.showDialog = true;
    },
    async saveMaterial() {
      this.isSaving = true;
      try {
        if (this.isEditing) {
          await axios.patch(`rmc_raw_materials/${this.editingId}`, this.form);
        } else {
          await axios.post("rmc_raw_materials", {
            ...this.form,
            company_id: 1,
            is_marketplace: true,
            quantity: 0,
            max_capacity: 0,
          });
        }
        this.showDialog = false;
        this.getData();
      } catch (e) {
        console.error("Error saving material:", e);
      }
      this.isSaving = false;
    },
    async deleteMaterial(id) {
      if (!confirm("Delete this marketplace item?")) return;
      try {
        await axios.post(`rmc_raw_materials/delete/${id}`);
        this.getData();
      } catch (e) {
        console.error("Error deleting:", e);
      }
    },
    categoryColor(cat) {
      const colors = { cement: "blue", aggregates: "brown", admixtures: "purple" };
      return colors[cat] || "grey";
    },
    formatNumber(num) {
      return new Intl.NumberFormat("en-IN").format(parseFloat(num) || 0);
    },
  },
};
</script>
